import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  convertUnixtTimeToDateTime,
  convertUnixtTimeToDateTimeSecond,
  convertBytes,
} from '../../lib/tools';
import * as types from '../../lib/constants';
import '../../styles/main.css';
import UserIcon from '../Parts/UserIcon';
import CredibilityCheckIcon from '../Parts/CredibilityCheckIcon';
import ColorSF2 from '../../styles/common/ColorSF2';
import parseTagNames from '../../functions/parseTagNames';

const style = {
  main: {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
    backgroundColor: ColorSF2.offWhite,
  },
  items: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    // fontSize: '0.8em',
  },
  block: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  img: {
    height: 100,
  },
  download: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  downloadLink: {
    color: '#333',
  },
  table: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `1px solid ${ColorSF2.mediumGray}`,
  },
  firstTable: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: 'none',
  },
  mediaInfoHeader: {
    width: 287,
    height: 40,
    backgroundColor: ColorSF2.paleBlue,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 16,
    marginTop: 5,
  },
  mediaInfoOperatorTitle: {
    width: 100,
    height: 90,
    backgroundColor: ColorSF2.paleBlue,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
    fontSize: 12,
  },
  mediaInfoOperatorData: {
    flex: '0 0 220px',
    height: 90,
    backgroundColor: ColorSF2.paleBlue,
    fontSize: 12,
    padding: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 'bold',
  },
  mediaInfoTitle: {
    flex: '0 0 100px',
    minHeight: 24,
    backgroundColor: ColorSF2.lightGray,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
    fontSize: 12,
  },
  mediaInfoData: {
    flex: '0 0 187px',
    minHeight: 24,
    backgroundColor: ColorSF2.white,
    fontSize: 12,
    padding: 9,
    display: 'flex',
    alignItems: 'center',
  },
  mediaInfoTagData: {
    display: 'block',
    maxHeight: 120,
    overflow: 'hidden auto',
    whiteSpace: 'break-spaces',
    overflowWrap: 'break-word',
  },
  mediaInfoSerial: {
    height: 'auto',
    minHeight: 24,
  },
  mediaInfoDataValue: {
    wordBreak: 'break-all',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mediaInfoHeaderDataLeft: {
    flex: 1,
    textAlign: 'left',
  },
  mediaInfoHeaderDataCenter: {
    flex: 1,
    textAlign: 'center',
  },
  mediaInfoHeaderDataRight: {
    flex: 1,
    textAlign: 'right',
  },
};

const gpsOffStatusMessages = {
  0: <FormattedMessage id="media.detail.gpsStatus.statusMessage.off.0" />,
  1: <FormattedMessage id="media.detail.gpsStatus.statusMessage.off.1" />,
  2: <FormattedMessage id="media.detail.gpsStatus.statusMessage.off.2" />,
};

const MediaInfo = ({ mediaInfo, operator, provider, userGroups }) => {
  const dispName = mediaInfo.operatorName || mediaInfo.userId;
  const mediaType = mediaInfo.mediaType || types.MEDIA_UNKNOWN;
  const isCansuke = provider && provider.isCansuke;
  const convertRecordedDate = isCansuke
    ? convertUnixtTimeToDateTimeSecond(mediaInfo.recordedDate)
    : convertUnixtTimeToDateTime(mediaInfo.recordedDate);
  const convertSavedDate = isCansuke
    ? convertUnixtTimeToDateTimeSecond(mediaInfo.savedDate)
    : convertUnixtTimeToDateTime(mediaInfo.savedDate);
  const convertFileSize = convertBytes(mediaInfo.fileSize);
  const messageIndex =
    (mediaInfo.gpsStatus !== null && mediaInfo.gpsStatus) || 0;
  const gpsStatus = gpsOffStatusMessages[messageIndex];
  const tagNames = parseTagNames(mediaInfo.tags);

  const groupName = userGroups.find(
    userGroup => userGroup.id === mediaInfo.group?.id,
  )?.name;

  return (
    <div style={style.main} className="mediaInfo">
      <div style={style.items}>
        <div style={style.firstTable}>
          <div style={style.mediaInfoOperatorTitle}>
            <UserIcon url={operator.iconUrl} size={60} />
          </div>
          <div style={style.mediaInfoOperatorData}>{dispName}</div>
        </div>
        <div style={style.mediaInfoHeader}>
          <div style={style.mediaInfoHeaderDataLeft}>&nbsp;</div>
          <div style={style.mediaInfoHeaderDataCenter}>
            <FormattedMessage id="media.description.title" />
            {mediaType === types.IMAGE && (
              <CredibilityCheckIcon
                checkResult={mediaInfo.credibilityCheckResult}
              />
            )}
          </div>
          <div style={style.mediaInfoHeaderDataRight}>&nbsp;</div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.recordedDate" />
          </div>
          <div style={style.mediaInfoData}>{convertRecordedDate}</div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.savedDate" />
          </div>
          <div style={style.mediaInfoData}>{convertSavedDate}</div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.fileSize" />
          </div>
          <div style={style.mediaInfoData}>{convertFileSize}</div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.deviceNickname" />
          </div>
          <div style={style.mediaInfoData}>{mediaInfo.deviceNickname}</div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.lat" />
          </div>
          <div style={style.mediaInfoData}>
            {(mediaInfo.lat === null && gpsStatus) || mediaInfo.lat}
          </div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.lng" />
          </div>
          <div style={style.mediaInfoData}>
            {(mediaInfo.lng === null && gpsStatus) || mediaInfo.lng}
          </div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.tag" />
          </div>
          <div
            style={Object.assign(
              {},
              ...[style.mediaInfoData, style.mediaInfoTagData],
            )}
          >
            {mediaInfo.freeTag && (
              <div>
                <font>{mediaInfo.freeTag}</font>
              </div>
            )}
            <div>
              {tagNames.map((levelTags, levelIndex) =>
                levelTags.map((tag, tagIndex) => {
                  const key = `tag-${levelIndex}-${tagIndex}`;
                  return (
                    <React.Fragment key={key}>
                      {tag}
                      {tagIndex < levelTags.length - 1 ? ' > ' : ''}
                      {tagIndex === levelTags.length - 1 &&
                      levelIndex < tagNames.length - 1
                        ? ', '
                        : ''}
                    </React.Fragment>
                  );
                }),
              )}
            </div>
          </div>
        </div>
        <div style={style.table}>
          <div style={style.mediaInfoTitle}>
            <FormattedMessage id="media.description.info.title.group" />
          </div>
          <div style={style.mediaInfoData}>{groupName || ''}</div>
        </div>
      </div>
    </div>
  );
};

MediaInfo.propTypes = {
  mediaInfo: PropTypes.object.isRequired,
  operator: PropTypes.object.isRequired,
  provider: PropTypes.object.isRequired,
  userGroups: PropTypes.array.isRequired,
};

export default MediaInfo;
